import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useTokens,
  usePropertyField,
} from '@vibesites/runtime/react';
import {
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { useComfyCtaStyles } from '../hooks/useComfyCtaStyles';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { pickHighestContrast, contrastRatio, relativeLuminance } from '../utils/luminance';

// ============================================================================
// Static slot group definitions (for contentSchema — must be inline literals)
// ============================================================================

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Explore Location CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/neighborhood' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Google Maps embed — same property-centred iframe as Blue Haven PinnedCallouts
// ============================================================================

const MAPS_API_KEY =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.GOOGLE_MAPS_API_KEY) || '';

// ============================================================================
// Component
// ============================================================================

export function NeighborhoodSection() {
  const cta = useSlotGroup('neighborhoodsection.cta_link', ctaLinkDef);

  // The section sits on the secondary color, which a property can override to
  // anything. Pick a legible text color against the actual resolved secondary
  // surface: prefer the theme's `textOnSecondary` (WCAG-tuned by the tokens
  // generator) with the body `text` color as the light-secondary alternative,
  // and fall back to synthesized pure black/white if neither hits WCAG AA —
  // so heading and body copy stay readable under any property override.
  const tokens = useTokens();
  const textColor = useMemo(() => {
    const c = tokens?.color;
    const sBg = c?.secondary || '#3E441D';
    const dark = c?.text || '#000000';
    const picked = pickHighestContrast(sBg, [c?.textOnSecondary || '#FFFFFF', dark]);
    if (picked && contrastRatio(sBg, picked) >= 4.5) return picked;
    return relativeLuminance(sBg) > 0.5 ? '#000000' : '#FFFFFF';
  }, [tokens]);

  useRevealStyles();
  useComfyCtaStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  // Property-centred map — mirrors Blue Haven PinnedCallouts / ContactMap embed.
  const lat = usePropertyField('property_address_latitude') as string | null;
  const lng = usePropertyField('property_address_longitude') as string | null;
  const address = usePropertyField('property_full_address') as string | null;

  const hasCoords = !!lat && !!lng && lat.trim() !== '' && lng.trim() !== '';

  let mapSrc = '';
  if (hasCoords && MAPS_API_KEY) {
    mapSrc = `https://www.google.com/maps/embed/v1/place?key=${MAPS_API_KEY}&q=${lat},${lng}&zoom=14`;
  } else if (hasCoords) {
    mapSrc = `https://maps.google.com/maps?q=${lat},${lng}&z=14&output=embed`;
  } else if (address && address.trim() !== '') {
    mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(address.trim())}&output=embed`;
  }

  // This section sits ON the secondary color, so the default secondary-fill CTA
  // would be invisible. Flip the resting fill to primary and swap to the page
  // background color on hover so the button stays legible against the section.
  const ctaColorVars = {
    '--comfy-cta-bg': 'var(--vibe-color-primary)',
    '--comfy-cta-fg': 'var(--vibe-color-text-on-primary)',
    '--comfy-cta-bg-hover': 'var(--vibe-color-background)',
    '--comfy-cta-fg-hover': 'var(--vibe-color-text)',
  } as CSSProperties;

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up relative w-full overflow-hidden bg-secondary py-14 md:py-20 lg:py-24"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-6 sm:px-8 lg:grid-cols-2 lg:gap-16 lg:px-12">
        {/* Left: copy */}
        <div className="relative z-10 max-w-xl lg:pl-4 xl:pl-10">
          <RichTextContent
            path="neighborhoodsection.heading"
            label="Heading"
            defaultValue="<p>The heart of a growing community</p>"
            as="h2"
            className="font-heading mb-6 text-3xl font-bold uppercase leading-[1.1] sm:text-4xl md:text-[2.75rem]"
            style={{ color: textColor }}
          />

          <RichTextContent
            path="neighborhoodsection.body"
            label="Body Text"
            defaultField="property_home_neighborhood_blurb"
            defaultValue="<p>Experience the best of downtown living with easy access to dining, entertainment, and cultural destinations. Our prime location puts you at the center of it all while offering a peaceful retreat to call home.</p>"
            as="div"
            className="font-body mb-10 max-w-lg text-base leading-relaxed [&_a]:text-primary [&_a]:underline"
            style={{ color: textColor }}
          />

          <EditableSlotGroup
            groupPath="neighborhoodsection.cta_link"
            definition={ctaLinkDef}
            className="inline-block"
          >
            <a
              href={cta.url || '/neighborhood'}
              target={cta._target || '_self'}
              className="neighborhood-cta comfy-cta"
              style={ctaColorVars}
            >
              <RichTextContent
                path="neighborhoodsection.cta_label"
                label="CTA Label"
                defaultValue="<p>Explore the Location</p>"
                as="span"
              />
            </a>
          </EditableSlotGroup>
        </div>

        {/* Right: Google Maps iframe (PinnedCallouts-style embed) */}
        <div className="relative z-10 w-full">
          <div className="neighborhood-map block w-full">
            {mapSrc ? (
              <iframe
                className="neighborhood-map__iframe"
                src={mapSrc}
                title="Property location map"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            ) : (
              <div className="neighborhood-map__empty">Map location not available</div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .neighborhood-cta [data-vibesite-richtext],
        .neighborhood-cta [data-vibesite-richtext] p,
        .neighborhood-cta [data-vibesite-richtext] span {
          margin: 0;
          font: inherit;
          color: inherit;
          text-transform: inherit;
          letter-spacing: inherit;
        }

        .neighborhood-map {
          position: relative;
          width: 100%;
          overflow: hidden;
          border-radius: 4px;
          background: #fff;
          box-shadow: 0 10px 30px rgba(52, 46, 31, 0.12);
        }

        .neighborhood-map__iframe {
          display: block;
          width: 100%;
          height: 420px;
          border: 0;
        }
        @media (min-width: 768px) {
          .neighborhood-map__iframe { height: 500px; }
        }

        .neighborhood-map__empty {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 420px;
          background: #f5f5f5;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          color: var(--vibe-color-text-muted);
        }
        @media (min-width: 768px) {
          .neighborhood-map__empty { height: 500px; }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodSection',
  displayName: 'Neighborhood Section',
  tokens: {
    'neighborhoodsection.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>The heart of a growing community</p>',
    },
    'neighborhoodsection.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_home_neighborhood_blurb',
      defaultValue:
        '<p>Experience the best of downtown living with easy access to dining, entertainment, and cultural destinations. Our prime location puts you at the center of it all while offering a peaceful retreat to call home.</p>',
    },
    'neighborhoodsection.cta_label': {
      label: 'CTA Label',
      type: 'richtext',
      defaultValue: '<p>Explore the Location</p>',
    },
  },
  slotGroups: {
    'neighborhoodsection.cta_link': ctaLinkDef,
  },
};

(NeighborhoodSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NeighborhoodSection;
